/**
 * Datadog Real User Monitoring (RUM) Configuration
 *
 * This module configures and initializes Datadog RUM for monitoring user interactions,
 * performance metrics, and application behavior in the Axle partner portal.
 *
 * Key Features:
 * - Automatic disabling in local development to prevent test data pollution
 * - Configurable override for local testing via environment variables
 * - Comprehensive tracking of user interactions, resources, and performance
 * - Session replay capabilities for debugging and user experience analysis
 *
 * Environment Variables Required:
 * - REACT_APP_DATADOG_RUM_APPLICATION_ID: Datadog application identifier
 * - REACT_APP_DATADOG_RUM_CLIENT_TOKEN: Client-side authentication token
 * - REACT_APP_DATADOG_IN_LOCAL: Optional override to enable in local development
 *
 * @see https://docs.datadoghq.com/real_user_monitoring/browser/
 */
import { PropagatorType, datadogRum } from '@datadog/browser-rum';

import { isLocal } from './utils/axleEnvironment';
import { graphQlPath } from './utils/axleUrls';
import { envVars } from './utils/envVars';

/**
 * Determines if Datadog monitoring should be enabled based on environment.
 *
 * Logic:
 * - Disabled by default in local development (isLocal = true)
 * - Can be overridden in local with REACT_APP_DATADOG_IN_LOCAL=true
 * - Always enabled in staging/production environments
 */
const datadogEnabled = !isLocal || envVars.datadogInLocal;

const propagatorTypes: PropagatorType[] = ['datadog', 'tracecontext'];
const firstPartyHosts = [
    {
        match: 'axlehealth.com',
        propagatorTypes: propagatorTypes,
    },
    {
        match: 'axlehealth-dev.com',
        propagatorTypes: propagatorTypes,
    },
    {
        match: 'axlehealth-staging.com',
        propagatorTypes: propagatorTypes,
    },
];
if (isLocal) {
    firstPartyHosts.push({
        match: 'localhost',
        propagatorTypes: propagatorTypes,
    });
}

/**
 * Initializes Datadog Real User Monitoring (RUM) with comprehensive tracking configuration.
 *
 * This function sets up client-side monitoring to collect:
 * - User interactions (clicks, page views, form submissions)
 * - Resource loading performance (API calls, assets, etc.)
 * - JavaScript errors and performance issues
 * - Session replays for debugging purposes
 *
 * The function automatically skips initialization in local development unless
 * explicitly overridden via environment variables to prevent test data from
 * polluting production monitoring dashboards.
 *
 * @returns {void} No return value - initializes global Datadog RUM instance
 *
 * @example
 * // Called once during application startup
 * initDatadog();
 *
 * @throws Will not throw errors - gracefully handles missing credentials
 */
export const initDatadog = () => {
    // Skip initialization in local development unless explicitly enabled
    if (!datadogEnabled) {
        console.info('[Datadog] Monitoring disabled for local development environment');
        return;
    }

    // Initialize Datadog RUM with comprehensive tracking configuration
    datadogRum.init({
        // Required: Application identification
        applicationId: envVars.datadogRumApplicationId,
        clientToken: envVars.datadogRumClientToken,

        // Datadog site configuration (US5 region)
        site: 'us5.datadoghq.com',

        // Service identification for filtering in Datadog dashboards
        service: 'dashboard',

        // Environment tag (development, staging, production)
        env: envVars.axleEnvironment,

        // Application version for release tracking (currently commented out)
        // version: '1.0.0',

        // Sampling configuration
        sessionSampleRate: 100, // Collect data from 100% of sessions
        sessionReplaySampleRate: 100, // Record session replays for 100% of sessions

        // Feature enablement flags
        trackUserInteractions: true, // Monitor clicks, form submissions, page navigation
        trackResources: true, // Monitor API calls, asset loading, network requests
        trackLongTasks: true, // Detect performance bottlenecks and blocking operations

        // Privacy configuration
        defaultPrivacyLevel: 'allow', // Allow collection of all data (can be 'mask' or 'mask-user-input')

        // GraphQL request tracking
        allowedGraphQlUrls: [
            {
                match: graphQlPath,
                trackPayload: true, // Include GraphQL query (limited to 32 KB)
                trackResponseErrors: true, // Capture GraphQL errors from responses
            },
        ],
        allowedTracingUrls: firstPartyHosts,
    });
};
